import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { StorageSpace } from '@/api/console';
import { Button, Callout, Checkbox, FormField, Input, Modal, useToast } from '@/components/ui';
import {
    buildQuotaBody,
    bytesToGb,
    formatBytes,
    saveErrorMessage,
    type QuotaMode,
} from './storageFormat';
import type { StorageSpacesVM } from './useStorageSpaces';
import styles from './StorageSpaces.module.css';

interface EditQuotaModalProps {
    /** The space being edited; `null` keeps the modal closed. */
    space: StorageSpace | null;
    mutation: StorageSpacesVM['quotaMutation'];
    onClose: () => void;
}

type Step = 'edit' | 'confirm';

function describeQuota(defaultLabel: string, value: number | null): string {
    return value === null ? defaultLabel : formatBytes(value);
}

/**
 * ECR-85: edit ONE space's quota override, in GB, with a two-step confirm.
 *
 * "Dùng mặc định của ứng dụng" sends `null`, which clears the override. The app's
 * default is only knowable while no override is set (it IS the effective quota
 * then); with an override in place the proxy carries no default figure, so the
 * modal says so instead of guessing.
 */
export function EditQuotaModal({ space, mutation, onClose }: EditQuotaModalProps) {
    const { t } = useTranslation();
    const toast = useToast();
    const [mode, setMode] = useState<QuotaMode>('default');
    const [gbText, setGbText] = useState('');
    const [step, setStep] = useState<Step>('edit');
    const [formError, setFormError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<{ key: string; detail?: string } | null>(null);

    useEffect(() => {
        if (!space) return;
        const hasOverride = space.quota_override_bytes !== null;
        setMode(hasOverride ? 'custom' : 'default');
        setGbText(
            String(
                bytesToGb(hasOverride ? (space.quota_override_bytes as number) : space.quota_bytes)
            )
        );
        setStep('edit');
        setFormError(null);
        setSaveError(null);
        mutation.reset();
        // ECR-85: reset on every OPEN (a new `space` object), including re-opening the
        // same row — keyed on the id alone, a second open kept the last attempt's input.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `mutation` is stable enough; resetting on its identity would loop
    }, [space]);

    if (!space) return null;

    const pending = mutation.isPending;
    const defaultLabel = t('console.storage.edit.appDefault');
    const built = buildQuotaBody(mode, gbText);
    const nextValue = built.ok ? built.body.quota_override_bytes : null;
    const unchanged = built.ok && nextValue === space.quota_override_bytes;

    const handleClose = () => {
        if (pending) return;
        onClose();
    };

    const handleReview = (event: FormEvent) => {
        event.preventDefault();
        if ('errorKey' in built) {
            setFormError(t(built.errorKey));
            return;
        }
        setFormError(null);
        setStep('confirm');
    };

    const handleConfirm = () => {
        if (!built.ok) return;
        setSaveError(null);
        mutation.mutate(
            { spaceId: space.id, quotaOverrideBytes: built.body.quota_override_bytes },
            {
                onSuccess: () => {
                    toast.success(t('console.storage.edit.saved'));
                    onClose();
                },
                onError: (error) => {
                    setSaveError(saveErrorMessage(error));
                    setStep('edit');
                },
            }
        );
    };

    const defaultHint =
        space.quota_override_bytes === null
            ? t('console.storage.edit.appDefaultKnown', { value: formatBytes(space.quota_bytes) })
            : t('console.storage.edit.appDefaultUnknown');

    let footer;
    if (step === 'confirm') {
        footer = (
            <>
                <Button onClick={() => setStep('edit')} disabled={pending}>
                    {t('console.storage.edit.back')}
                </Button>
                <Button type="primary" onClick={handleConfirm} loading={pending}>
                    {t('console.storage.edit.confirm')}
                </Button>
            </>
        );
    } else {
        footer = (
            <>
                <Button onClick={handleClose} disabled={pending}>
                    {t('common.cancel')}
                </Button>
                <Button
                    type="primary"
                    htmlType="submit"
                    form="storage-quota-form"
                    disabled={unchanged}
                >
                    {t('console.storage.edit.review')}
                </Button>
            </>
        );
    }

    return (
        <Modal
            open
            onCancel={handleClose}
            title={t('console.storage.edit.title')}
            width={480}
            footer={footer}
        >
            <dl className={styles.summary}>
                <dt>{t('console.storage.table.organization')}</dt>
                <dd>{space.organization_name || t('console.storage.table.unnamedOrg')}</dd>
                <dt>{t('console.storage.table.app')}</dt>
                <dd>
                    <code>{space.app_code}</code> · {space.scope_id}
                </dd>
                <dt>{t('console.storage.edit.currentQuota')}</dt>
                <dd>{formatBytes(space.quota_bytes)}</dd>
                <dt>{t('console.storage.edit.currentOverride')}</dt>
                <dd>{describeQuota(defaultLabel, space.quota_override_bytes)}</dd>
                <dt>{t('console.storage.edit.used')}</dt>
                <dd>{formatBytes(space.used_bytes + space.reserved_bytes)}</dd>
            </dl>

            {saveError && (
                <Callout tone="danger">
                    {t(saveError.key)}
                    {saveError.detail ? ` — ${saveError.detail}` : ''}
                </Callout>
            )}

            {step === 'confirm' ? (
                <Callout tone="warning">
                    {t('console.storage.edit.confirmText', {
                        from: describeQuota(defaultLabel, space.quota_override_bytes),
                        to: describeQuota(defaultLabel, nextValue),
                    })}
                </Callout>
            ) : (
                <form id="storage-quota-form" className={styles.form} onSubmit={handleReview}>
                    <Checkbox
                        checked={mode === 'default'}
                        onChange={(e) => setMode(e.target.checked ? 'default' : 'custom')}
                    >
                        {t('console.storage.edit.useDefault')}
                    </Checkbox>
                    <FormField
                        label={t('console.storage.edit.quotaGb')}
                        hint={mode === 'default' ? defaultHint : undefined}
                        error={formError ?? undefined}
                    >
                        <Input
                            type="number"
                            min={0}
                            step="any"
                            inputMode="decimal"
                            value={gbText}
                            disabled={mode === 'default'}
                            onChange={(e) => setGbText(e.target.value)}
                            suffix="GB"
                            aria-label={t('console.storage.edit.quotaGb')}
                        />
                    </FormField>
                </form>
            )}
        </Modal>
    );
}
